import React, { useEffect, useState } from "react";

/**
 * CornerBrandMark — small persistent red-glass / gold logo that
 * fades into the top-left of every TV stage AFTER the SplashFlash
 * "presents" moment exits. Stays visible the whole time the video
 * plays so the brand never leaves the screen entirely — it just
 * tucks itself out of the way.
 *
 * Pure decoration: `pointer-events: none` + `aria-hidden` so it
 * never intercepts taps on player controls or interferes with
 * fullscreen behaviour.
 *
 * Props:
 *   delayMs – ms to wait after mount before fading in. Defaults to
 *             1300 so the splash (≈1.3s) is fully gone first.
 *   size    – pixel dimension. Defaults to 36 — small enough to be
 *             a courtesy mark, big enough to be recognisable.
 */
export default function CornerBrandMark({ delayMs = 1300, size = 36 }) {
  const [visible, setVisible] = useState(delayMs === 0);

  useEffect(() => {
    if (delayMs === 0) return undefined;
    const t = setTimeout(() => setVisible(true), delayMs);
    return () => clearTimeout(t);
  }, [delayMs]);

  return (
    <div
      aria-hidden="true"
      data-testid="corner-brand-mark"
      className="absolute left-2 z-20 pointer-events-none select-none"
      style={{
        // Push the mark well below the device safe-area (Android
        // status bar / iPhone notch / Dynamic Island) so it never
        // gets clipped — and not glued to the very top edge. Adds a
        // generous 14px cushion on top of the OS safe-area inset so
        // the logo sits comfortably inside the player chrome
        // instead of fighting the status bar / notch hardware.
        top: "calc(env(safe-area-inset-top, 0px) + 14px)",
        width: size,
        height: size,
        opacity: visible ? 0.85 : 0,
        transition: "opacity 500ms ease",
      }}
    >
      <img
        src="/icons/icon-512.png?v=3"
        alt=""
        width={size}
        height={size}
        loading="eager"
        decoding="async"
        style={{
          width: "100%",
          height: "100%",
          objectFit: "cover",
          borderRadius: "22%",
          boxShadow: "0 2px 8px rgba(0,0,0,0.55)",
        }}
      />
    </div>
  );
}
